import { useEffect } from 'react';
import { authApiService } from '../api/auth.api';
import { authSession, useAuthStore } from '../stores/auth.store';

/**
 * Load the signed-in user's profile (`GET /v1/me`) into the auth store.
 *
 * Best-effort: a failure leaves the store untouched and returns `false` — the
 * session itself is still valid (`useEnsureUserProfile` retries later). The
 * result is dropped if the session was cleared while the request was in flight
 * (logout), so a stale profile can never be written into an empty store.
 */
export async function hydrateUserProfile(): Promise<boolean> {
  try {
    const user = await authApiService.me();
    if (!authSession.isAuthenticated()) return false;
    useAuthStore.getState().setUser(user);
    return true;
  } catch {
    return false;
  }
}

/**
 * Safety net for the authenticated shell: whenever we hold tokens but no
 * profile, fetch it in the background.
 *
 * `useSessionBootstrap` runs exactly once at app start — BEFORE the OAuth
 * callback stores the tokens of a fresh login — so it cannot cover the
 * post-login case. The callback hydrates the profile itself; this hook covers
 * the cases where that call failed (or any other path that sets tokens without
 * a profile), so the sidebar identity never needs a manual page refresh.
 */
export function useEnsureUserProfile(): void {
  const hasTokens = useAuthStore((s) => Boolean(s.tokens?.accessToken));
  const hasUser = useAuthStore((s) => s.user !== null);

  useEffect(() => {
    if (!hasTokens || hasUser) return;
    void hydrateUserProfile();
  }, [hasTokens, hasUser]);
}
